/**
 * The capability vocabulary a grant can actually carry (ADR-057, S-395).
 *
 * # This replaces an invented catalog, and the difference is not cosmetic
 *
 * `permissions.ts` defined `resource:action` strings — `targets:scan`,
 * `vulnerabilities:retest`, `api-keys:manage` and eleven more resources. None of
 * them is a relation the authorization model defines. They were a design for a
 * backend that did not exist yet, and their own header said so: *"intended for
 * backend enforcement once a server exists"*. The server exists, and it defines
 * something else.
 *
 * Offering those strings on a live Roles page would be
 * `client-side-rosters-are-guesses` with authority attached: an administrator
 * ticking `vulnerabilities:push` would be told they had granted something, and
 * the platform would have no relation by that name to grant. `role.Grantable` is
 * a CLOSED set at the API, so the request would be refused at the decision point
 * — the best case is an error the administrator cannot act on, and the worst is a
 * page that looks like it worked.
 *
 * # The list is the platform's, not this file's
 *
 * These six names are `authz.ProjectCapabilities` plus the two relations that
 * predate ADR-057. They are written here because TypeScript cannot import Go —
 * and the OpenAPI contract (`RoleGrant.capability`) publishes the same closed
 * enum, so the server refuses anything this file gets wrong rather than acting on
 * it. A name added to the model reaches this file through that enum, and the
 * failure mode of forgetting is a capability the console cannot offer, never one
 * it offers falsely.
 */

/** A relation a grant can carry. The server's enum is the authority. */
export type Capability =
  | "admin"
  | "viewer"
  | "can_configure_targets"
  | "can_launch_scan"
  | "can_manage_issues"
  | "can_request_reports";

export interface CapabilityInfo {
  id: Capability;
  label: string;
  /** What holding it lets a person DO, in the words an administrator needs. */
  description: string;
}

/**
 * Ordered widest-first, because that is the order the decision is made in: an
 * administrator asks "should this role administer the project" before asking
 * which narrower things it should carry.
 */
export const CAPABILITIES: CapabilityInfo[] = [
  {
    id: "admin",
    label: "Project administrator",
    // 3.107: shown as a column tooltip, so plain words and no capitals for
    // emphasis. "Not authority to work in it" went too: admin INCLUDES the four
    // working capabilities (IMPLIED_BY_ADMIN), so that clause was untrue.
    description:
      "Everything the other capabilities allow, plus renaming and deleting the project and " +
      "granting capabilities to other roles.",
  },
  {
    id: "viewer",
    label: "Read only",
    description:
      "Read the project and everything in it. Every member of the organisation already reads this much, " +
      "so granting it to a role adds nothing they did not have.",
  },
  {
    id: "can_configure_targets",
    label: "Configure targets",
    description: "Create and change targets and authentication profiles.",
  },
  {
    id: "can_launch_scan",
    label: "Launch and stop scans",
    description:
      "Start a scan and abort one. Aborting is not limited to scans this person started — a holder can " +
      "stop any run in the project, including one an administrator launched.",
  },
  {
    id: "can_manage_issues",
    label: "Manage issues",
    // 3.107: the backticks printed literally in the tooltip. Same fact, plain words.
    description:
      "Move an issue through its lifecycle, except to Verified, which only the Validation Engine " +
      "can set and no person can.",
  },
  {
    id: "can_request_reports",
    label: "Request reports",
    description: "Ask for a report and download it.",
  },
];

/**
 * 2.667 — what the console OFFERS to grant: the vocabulary minus `viewer`.
 *
 * `viewer` stays in `CAPABILITIES` so a grant the platform holds is still named,
 * but it is not offered, because it adds nothing. In the model
 * (`control/internal/authz/model.fga`) a project's `viewer` is
 * `[role#assignee] or member from workspace`, and a workspace's `member` inherits
 * from the organisation. So every member, and every API key once it joins the
 * graph on its first request, already reads every project. Offering it invited
 * administrators to build "read-only auditor" roles that restrict nothing. If
 * the member baseline is ever narrowed, add it back here.
 */
export const GRANTABLE_CAPABILITIES: CapabilityInfo[] = CAPABILITIES.filter((c) => c.id !== "viewer");

const BY_ID = new Map(CAPABILITIES.map((c) => [c.id, c]));

/**
 * The label for a capability the SERVER named.
 *
 * Returns the raw id for a name this file does not know rather than throwing or
 * hiding it. A grant the platform holds and this console cannot name is a real
 * grant, and showing `can_manage_widgets` is honest; dropping it from the list
 * would tell an administrator a role holds less than it does, which is the one
 * direction a permissions screen must never be wrong in.
 */
export function capabilityLabel(id: string): string {
  return BY_ID.get(id as Capability)?.label ?? id;
}

/** Whether this console has a description for a server-named capability. */
export function isKnownCapability(id: string): id is Capability {
  return BY_ID.has(id as Capability);
}
